import { ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useUpdateOohUpgrade } from '@/api/domains/markets';
import { useMe } from '@/api/domains/merchant-settings';
import type { Market } from '@/api/types/services';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { toApiProblem, toastProblem } from '@/lib/errors';
import { cn } from '@/lib/utils';

import { CountryFlags } from '../markets/components/CountryFlags';
import { OohPopupPreview } from './OohPopupPreview';

/**
 * One market section (Figma 712-17518): collapsible header (name + flags + chevron)
 * over a grey panel holding the OOH pop-up activation toggle, the checkout-card
 * preview and a per-market Save.
 */
export function OohPopupMarketSection({ market }: { market: Market }) {
  const { t } = useTranslation('widgets');
  // Tolerate a stale cached market shape that predates the ooh_upgrade field.
  const ooh = market.ooh_upgrade ?? { active: false };

  const [open, setOpen] = useState(true);
  const [active, setActive] = useState(ooh.active);

  // The pop-up renders in the main checkout (Plus-only), so activation is gated -
  // mirrors the server-side check and the checkout_ext Plus gate on /authorization.
  const isShopifyPlus = useMe().data?.merchant?.is_shopify_plus ?? false;

  const update = useUpdateOohUpgrade(market.id);

  const handleSave = () => {
    update.mutate(
      { active },
      {
        onSuccess: () => toast.success(t('oohPopup.saved')),
        onError: (error) => toastProblem(toApiProblem(error)),
      },
    );
  };

  return (
    <section>
      <div className="border-t border-[#ededed]" />
      <div className="flex items-center justify-between py-4">
        <div className="flex min-w-0 flex-col gap-[3px]">
          <h3 className="truncate text-xl font-medium tracking-[-0.6px] text-black">
            {market.name}
          </h3>
          <CountryFlags codes={market.countries} />
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={open ? t('oohPopup.collapse') : t('oohPopup.expand')}
          className="flex h-9 w-10 items-center justify-center rounded-md text-foreground transition-colors hover:bg-accent"
        >
          <ChevronUp className={cn('size-6 transition-transform', !open && 'rotate-180')} />
        </button>
      </div>
      <div className="border-t border-[#ededed]" />

      {open ? (
        <div className="bg-[#f7f7f7] px-6 py-8">
          <div className="flex w-full max-w-[695px] flex-col gap-[21px]">
            <div className="flex flex-col gap-3.5">
              <h4 className="text-xl font-medium tracking-[-0.6px] text-black">
                {t('oohPopup.widgetTitle')}
              </h4>
              <p className="text-base text-muted-foreground">{t('oohPopup.requiresPlus')}</p>
              <div className="flex items-center gap-2.5">
                <Switch
                  checked={active}
                  onCheckedChange={setActive}
                  disabled={!isShopifyPlus}
                  aria-label={t('oohPopup.toggleLabel')}
                />
                <span className="text-sm font-medium text-[#171717]">{t('oohPopup.active')}</span>
              </div>
            </div>

            <div className="flex max-w-[469px] flex-col gap-3">
              <p className="text-sm font-medium text-[#0a0a0a]">{t('oohPopup.preview')}</p>
              <OohPopupPreview />
            </div>

            <Button
              type="button"
              onClick={handleSave}
              disabled={update.isPending || !isShopifyPlus}
              className="w-fit bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {t('oohPopup.save')}
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
